import { useSelector } from "react-redux";

import type { RootState } from "~/app/store";
import {
  selectCheckoutCart,
  selectTotalCheckoutValue,
  selectTotalWeight,
} from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { selectTotalCartPriceWithoutDiscount } from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  selectCartContainsSurcharge,
  selectCartContainsTariffSurcharge,
  selectCartSurcharge,
} from "~/features/cart/cart/redux/selectors/selectors";
import { selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import type { ProductTableViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import {
  buildOptionalPartDescriptorMap,
  buildProductTableViewModel,
  collectAllSubLineItems,
} from "~/features/cart/cartV2/utils/buildProductTableViewModel";
import { selectResolvedGroupedEntryV2 } from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";

function buildCheckoutTableViewModel(
  state: RootState,
  group:
    | ReturnType<typeof selectCheckoutCart>["orderables"]
    | ReturnType<typeof selectCheckoutCart>["bundles"][number]
    | ReturnType<typeof selectCheckoutCart>["quotables"],
): ProductTableViewModel {
  const resolvedEntries = group.entries.map((entry) => selectResolvedGroupedEntryV2(entry)(state));
  const packageEntryMap = selectPackageEntryMap(state);
  const allSubLineItems = collectAllSubLineItems(resolvedEntries, packageEntryMap);
  const optionalPartDescriptors = buildOptionalPartDescriptorMap(state, allSubLineItems);

  return buildProductTableViewModel({
    group,
    mode: "review",
    resolvedEntries,
    packageEntryMap,
    optionalPartDescriptors,
    alternateRows: [],
  });
}

export function useCheckoutCartViewModel() {
  const cartData = useSelector(selectCheckoutCart);
  const totalWeight = useSelector(selectTotalWeight);
  const totalSum = useSelector(selectTotalCheckoutValue);
  const totalDiscount = useSelector(selectTotalCartPriceWithoutDiscount);
  const hasAnySurcharge = useSelector(selectCartContainsSurcharge);
  const hasAnyTariffSurcharge = useSelector(selectCartContainsTariffSurcharge);
  const surchargeValue = useSelector(selectCartSurcharge);

  const orderablesViewModel = useSelector((state: RootState) =>
    buildCheckoutTableViewModel(state, cartData.orderables),
  );
  const bundleViewModels = useSelector((state: RootState) =>
    cartData.bundles.map((bundle) => buildCheckoutTableViewModel(state, bundle)),
  );
  const quotablesViewModel = useSelector((state: RootState) => buildCheckoutTableViewModel(state, cartData.quotables));

  return {
    cartData,
    totalWeight,
    totalSum,
    totalDiscount,
    hasAnySurcharge,
    hasAnyTariffSurcharge,
    surchargeValue,
    orderablesViewModel,
    bundleViewModels,
    quotablesViewModel,
  };
}
